import * as React from 'react';
import MuiTextField from '@mui/material/TextField';
import { useState, useEffect } from 'react';
import { ITextFieldProps } from './text-field-props';
import { sanitizeInput } from '../../utilities/text-sanitizer';

export function TextField(props: ITextFieldProps) {
  const {
    children,
    errorText = '',
    variant = 'filled',
    lazy,
    className,
    disabled = false,
    error = false,
    fullWidth = true,
    helperText,
    InputLabelProps,
    InputProps,
    inputProps,
    inputRef,
    label = 'Label',
    maxLength,
    multiline = true,
    name,
    onBlur,
    onChange,
    onClick,
    onKeyDown,
    onKeyUp,
    readOnly = false,
    required = false,
    rows,
    maxRows = 6,
    minRows,
    select,
    size,
    sx,
    type,
    value,
    FormHelperTextProps,
    autoFocus = false,
    title,
    style,
    placeholder,
  } = props;

  const [stateValue, setStateValue] = useState(value);

  useEffect(() => {
    if (lazy) {
      setStateValue(value);
    }
  }, [value, lazy]);

  // Build InputLabelProps
  let computedInputLabelProps = InputLabelProps || {};
  if (variant === 'outlined') {
    computedInputLabelProps = { ...computedInputLabelProps, shrink: true };
  }
  if (required && readOnly) {
    computedInputLabelProps = { ...computedInputLabelProps, required: false };
  }

  // Build InputProps
  let computedInputProps = InputProps || {};
  if (readOnly) {
    computedInputProps = { ...computedInputProps, readOnly: readOnly };
  }

  // Build inputProps
  let computedInputProps2 = inputProps || {};
  if (maxLength) {
    computedInputProps2 = { ...computedInputProps2, maxLength: maxLength };
  }
  computedInputProps2 = { ...computedInputProps2, autoComplete: 'off' };

  // Compute data-disabled-state
  const dataDisabledState = disabled || readOnly ? true : undefined;

  // Compute rows/minRows
  let computedRows = rows;
  let computedMinRows = minRows;
  if (maxRows && rows) {
    if (!minRows) {
      computedMinRows = rows;
    }
    computedRows = undefined;
  }

  // Compute helperText
  const computedHelperText = errorText && error ? errorText : helperText;

  const text = lazy ? stateValue : value;
  const cleanedText = React.useMemo(() => sanitizeInput(text), [text]);

  function handleChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    if (lazy) {
      setStateValue(event.target.value);
    } else {
      onChange && onChange(event);
    }
  }

  function handleBlur(event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) {
    lazy && onChange && onChange(event);
    onBlur && onBlur(event);
  }

  return (
    <MuiTextField
      variant={variant}
      className={className}
      disabled={disabled}
      error={error}
      fullWidth={fullWidth}
      helperText={computedHelperText}
      InputLabelProps={computedInputLabelProps}
      InputProps={computedInputProps}
      inputProps={computedInputProps2}
      inputRef={inputRef}
      label={label}
      multiline={multiline}
      name={name}
      onClick={onClick}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
      required={required}
      rows={computedRows}
      maxRows={maxRows}
      minRows={computedMinRows}
      select={select}
      size={size}
      sx={sx}
      type={type}
      FormHelperTextProps={FormHelperTextProps}
      autoFocus={autoFocus}
      title={title}
      style={style}
      placeholder={placeholder}
      data-disabled-state={dataDisabledState}
      value={cleanedText}
      onChange={handleChange}
      onBlur={handleBlur}
    >
      {children}
    </MuiTextField>
  );
}
